import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import './styles.scss';

// ─────────────────────────────────────────────────────────────────────────────
// Static data
// ─────────────────────────────────────────────────────────────────────────────

const featureCards = [
  // {
  //   tag: 'Government Construction',
  //   title: 'Job-Costed Financial Logic',
  //   description:
  //     "Stop fund commingling by design. LusidPay's automated logic tags every invoice to specific Govt. Project IDs, enforcing rules that ensure capital is released strictly for materials, blocking overhead leakage programmatically.",
  //   icon: (
  //     <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
  //       <path d="M2 22h20"/><path d="M4 22V9l8-5 8 5v13"/><path d="M12 4v18"/><path d="M8 10v12"/><path d="M16 10v12"/>
  //     </svg>
  //   ),
  // },
  {
    tag: 'Compliance Core',
    title: 'Entity Telemetry Protocol',
    description:
      'Leverage our proprietary verification engine to synchronize multi-jurisdictional entity data. We utilize a decentralized consensus model to validate certification requirements against federal frameworks, ensuring immutable compliance readiness.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/>
      </svg>
    ),
  },
  {
    tag: 'Global Trade & Logistics',
    title: 'Global Supply Chain Sync',
    description:
      'Unified infrastructure for Importers and Freight Forwarders. Manage multi-currency invoices and settle international supplier accounts instantly at wholesale conversion rates, integrating logistics data directly into the project budget.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    ),
  },
];


const testimonials = [
  {
    quote:
      '"Historically, our cross-border capital was disconnected from our customs declarations, creating a massive blind spot for TBML and audit liability. LusidPay’s conditional allocation engine solved this instantly. By mathematically tying our international disbursements directly to verified 19 CFR data, we automated our Form 28 defense."',
    author: '- Chief Financial Officer, Top 500 U.S. Importer',
  },
  {
    quote: '"The Trade Compliance OS acts as our ultimate liability shield. It ingests massive, unstructured BOMs and executes zero-hallucination semantic extraction before anything hits CBP ACE. Fusing that 19 CFR clearance with synchronized settlement has completely transformed how we manage global risk."',
    author: '– VP of Trade Compliance & Treasury, Global Logistics Firm',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const HomePage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // ── Slider state ──────────────────────────────────────────────────────────
  // eslint-disable-next-line no-unused-vars
  const [activeSlide, setActiveSlide] = useState(0);
  const slideIntervalRef = useRef(null);

  // ── Scroll to top on mount ─────────────────────────────────────────────────
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // ── Scroll to hash ────────────────────────────────────────────────────────
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [location]);

  // ── Hero slider auto-advance ───────────────────────────────────────────────
  const startSlider = () => {
    clearInterval(slideIntervalRef.current);
    slideIntervalRef.current = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % 2);
    }, 6000);
  };

  useEffect(() => {
    startSlider();
    return () => clearInterval(slideIntervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box className="home-page">

      {/* ══════════════════════════════════════════════════════
          NEW SECTION (from new_home_page.html) — TOPMOST
      ══════════════════════════════════════════════════════ */}

      {/* ── New Hero ─────────────────────────────────────────── */}
      <Box className="nh-hero">
        <Box className="nh-hero__grid-bg" />
        <Container maxWidth="lg" className="nh-hero__inner">
          <Typography className="nh-hero__title"
            sx={{marginTop: "30px" }}>
            Unifying AI Compliance with{' '}
            <Box component="span" className="nh-hero__accent">
              Programmable Ledger Allocations.
            </Box>
          </Typography>
          <Typography className="nh-hero__subtitle">
            LusidPay engineers multi-billion dollar trade and logistics infrastructure. We pair zero-hallucination semantic extraction engines with conditional fund disbursements to eliminate regulatory liability and synchronize global settlement.
          </Typography>
          <Box className="nh-hero__cta-row">
            <Button
              variant="contained"
              className="nh-hero__btn nh-hero__btn--primary"
              onClick={() => navigate('/trade-secure')}
            >
              Explore Trade Secure
            </Button>
            <Button
              variant="outlined"
              className="nh-hero__btn nh-hero__btn--secondary"
              onClick={() => navigate('/platform-architecture')}
            >
              View Architecture
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── Deployed Nodes Section ───────────────────────────── */}
      <Box className="nh-nodes">
        <Container maxWidth="lg">
          <Box className="nh-nodes__header">
            <Typography className="nh-nodes__title">
              Deployed Across High-Stakes Nodes
            </Typography>
            <Typography className="nh-nodes__subtitle">
              Our conditional allocation engines are specifically tailored for complex, multi-party regulatory environments.
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {/* Trade Secure */}
            <Grid item xs={12} md={8}>
              <Box className="nh-node-card nh-node-card--live">
                <Box className="nh-node-card__header">
                  <Typography className="nh-node-card__sector nh-node-card__sector--sky">
                    Global Logistics
                  </Typography>
                  <Box className="nh-node-card__badge nh-node-card__badge--live">Live</Box>
                </Box>
                <Typography className="nh-node-card__name">Trade Secure</Typography>
                <Typography className="nh-node-card__desc">
                  Built for Large Importers and Customs Brokers. Ingests 500-page BOMs, performs deterministic math against draft CBP Form 7501s to ensure absolute 19 CFR compliance, and generates cryptographic audit dossiers.
                </Typography>
                <Button
                  className="nh-node-card__link"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/trade-secure')}
                  sx={{ padding: "20px"}}
                >
                  View Detail
                </Button>
              </Box>
            </Grid>

            {/* Build Secure */}
            {/* <Grid item xs={12} md={6}>
              <Box className="nh-node-card nh-node-card--restricted">
                <Box className="nh-node-card__header">
                  <Typography className="nh-node-card__sector nh-node-card__sector--muted">
                    Govt. Construction
                  </Typography>
                  <Box className="nh-node-card__badge nh-node-card__badge--beta">Private Beta</Box>
                </Box>
                <Typography className="nh-node-card__name">Build Secure</Typography>
                <Typography className="nh-node-card__desc">
                  Built for B2G Prime Contractors and Municipal Agencies. Automates DBE/ACDBE certification workflows and synchronizes subcontractor capital disbursements directly to verified milestone data.
                </Typography>
                <Box className="nh-node-card__restricted">
                  <LockIcon fontSize="small" />
                  <span>Access Restricted</span>
                </Box>
              </Box>
            </Grid> */}
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════════
          EXISTING SECTIONS — REFACTORED
      ══════════════════════════════════════════════════════ */}

      {/* ── Hero Slider ──────────────────────────────────────── */}
      {/* <Box className="hero-slider">
        <Box className={`hero-slider__slide${activeSlide === 0 ? ' hero-slider__slide--active' : ''}`}>
          <Container maxWidth="lg">
            <Box className="hero-slider__content">
              <Typography className="hero-slider__title">
                The Financial Operating System for Construction &amp; Global Trade.
              </Typography>
              <Typography className="hero-slider__body">
                Unify financial workflows across the physical economy. From Government Contractors to Freight Forwarders and Global Importers — synchronize AR/AP automation, compliance data, and cross-border capital in one infrastructure.
              </Typography>
              <Box className="hero-slider__badges">
                <span>Govt. Construction</span>
                <span className="hero-slider__bullet">•</span>
                <span>Global Supply Chain</span>
                <span className="hero-slider__bullet">•</span>
                <span>Freight Forwarders</span>
              </Box>
              <Box className="hero-slider__actions">
                <Button
                  variant="contained"
                  className="hero-slider__btn"
                  onClick={() => navigate('/contact-us')}
                >
                  Request Platform Demo
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
        <Box className={`hero-slider__slide${activeSlide === 1 ? ' hero-slider__slide--active' : ''}`}>
          <Container maxWidth="lg">
            <Box className="hero-slider__content">
              <Typography className="hero-slider__title">
                The Fast Lane for QuickBooks Desktop.
              </Typography>
              <Typography className="hero-slider__body" style={{ marginBottom: '20px' }}>
                Stop waiting 4 days for payments. Deliver Next Day ACH for the standard price. Unlimited Users. Free Accounting Sync. Funds secured at U.S. Bank.
              </Typography>
              <Box className="hero-slider__trust-badge">
                ✅ Works seamlessly with{' '}
                <strong>QuickBooks Pro/Premier/Enterprise 2015-2024, and QuickBooks Online.</strong>
              </Box>
              <Box className="hero-slider__actions">
                <Button
                  variant="contained"
                  className="hero-slider__btn"
                  onClick={() => {
                    const el = document.getElementById('how-it-works');
                    if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
                  }}
                >
                  See How It Works
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
        <Box className="hero-slider__dots">
          <Box
            className={`hero-slider__dot${activeSlide === 0 ? ' hero-slider__dot--active' : ''}`}
            onClick={() => goToSlide(0)}
          />
          <Box
            className={`hero-slider__dot${activeSlide === 1 ? ' hero-slider__dot--active' : ''}`}
            onClick={() => goToSlide(1)}
          />
        </Box>
      </Box> */}

      {/* ── Unified Financial Infrastructure Features ─────────── */}
      <Box className="features-section">
        <Container maxWidth="lg">
          <Box className="features-section__header">
            <Typography className="features-section__title">
              Unified Financial Infrastructure
            </Typography>
            <Typography className="features-section__subtitle">
              Orchestrate complex capital flows with programmable logic. Replace disconnected spreadsheets/systems and static banking portals with an automated financial operating system.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {featureCards.map((card) => (
              <Grid item xs={12} md={6} key={card.title}>
                <Box className="feature-card">
                  <Typography className="feature-card__tag">{card.tag}</Typography>
                  <Box className="feature-card__icon-box">{card.icon}</Box>
                  <Typography className="feature-card__title">{card.title}</Typography>
                  <Typography className="feature-card__body">{card.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Why LusidPay ─────────────────────────────────────── */}
      {/* <Box className="trust-section" id="trust">
        <Container maxWidth="lg">
          <Typography className="home-section-title">Why LusidPay?</Typography>
          <Grid container spacing={3} justifyContent="center" className="trust-section__grid">
            {trustBlocks.map((text) => (
              <Grid item xs={12} sm={4} key={text}>
                <Box className="trust-block">{text}</Box>
              </Grid>
            ))}
          </Grid>
          <Typography className="trust-section__security-note">
            Bank-grade encryption&nbsp;|&nbsp;Multi-factor authentication&nbsp;|&nbsp;Regular third-party audits
          </Typography>
        </Container>
      </Box> */}

      {/* ── Testimonials ─────────────────────────────────────── */}
      <Box className="testimonials-section" id="testimonials">
        <Container maxWidth="md">
          <Typography className="home-section-title">What Our Clients Say</Typography>
          {testimonials.map((t) => (
            <Box className="testimonial-card" key={t.author}>
              <Typography className="testimonial-card__quote">{t.quote}</Typography>
              <Typography className="testimonial-card__author">{t.author}</Typography>
            </Box>
          ))}
        </Container>
      </Box>

      {/* ── CTA Section ──────────────────────────────────────── */}
      <Box className="home-cta-section">
        <Container maxWidth="md">
          <Typography className="home-cta-section__title">
            Ready to transform your finance operations?
          </Typography>
          <Button
            variant="contained"
            className="home-cta-section__btn"
            onClick={() => navigate('/contact-us')}
          >
            Contact Sales
          </Button>
        </Container>
      </Box>

    </Box>
  );
};

export default HomePage;
